import React from 'react';
import { Form } from 'components/atoms/form';
import { unreachable } from 'utils/unreachable';
import { FieldType } from '../fields';
import {
  GetFilterOperationTypeOptions,
  GetOperationOptionRenderer,
  GetSelectEditorComponents,
} from '../filter-box/utils';
import { FieldFilter } from '../filters';
import { AssigneeFieldFilter } from '../filters/assignee';
import { BooleanFieldFilter } from '../filters/boolean';
import { DateFieldFilter } from '../filters/date';
import { EstimateFieldFilter } from '../filters/estimate';
import { MultiSelectFieldFilter } from '../filters/multi-select';
import { MultiUserFieldFilter } from '../filters/multi-user';
import { NumberFieldFilter } from '../filters/number';
import { SingleSelectFieldFilter } from '../filters/single-select';
import { SingleUserFieldFilter } from '../filters/single-user';
import { TextFieldFilter } from '../filters/text';
import { AssigneeCriteria } from './assignee';
import { BooleanCriteria } from './boolean';
import { DateCriteria } from './date';
import { EstimateCriteria } from './estimate';
import { MultiSelectCriteria } from './multi-select';
import { MultiUserCriteria } from './multi-user';
import { NumberCriteria } from './number';
import { ResponsibleCriteria } from './responsible';
import { SingleSelectCriteria } from './single-select';
import { SingleUserCriteria } from './single-user';
import { TextCriteria } from './text';

interface Props {
  filter?: FieldFilter;
  onChange: (filter: FieldFilter) => void;
  getFilterOperationTypeOptions?: GetFilterOperationTypeOptions;
  getSelectEditorComponents?: GetSelectEditorComponents;
  getOperationOptionRenderer?: GetOperationOptionRenderer;
}

export const criterion = ({
  filter,
  onChange,
  getFilterOperationTypeOptions,
  getSelectEditorComponents,
  getOperationOptionRenderer,
}: Props): React.ReactNode => {
  if (!filter) {
    return null;
  }

  switch (filter.field.type) {
    case FieldType.Boolean:
      return (
        <BooleanCriteria
          filter={filter as BooleanFieldFilter}
          onChange={onChange}
          getFilterOperationTypeOptions={
            getFilterOperationTypeOptions?.[FieldType.Boolean]
          }
          getOperationOptionRenderer={
            getOperationOptionRenderer?.BooleanFilterOperationType
          }
        />
      );
    case FieldType.Number:
      return (
        <NumberCriteria
          filter={filter as NumberFieldFilter}
          onChange={onChange}
          getFilterOperationTypeOptions={
            getFilterOperationTypeOptions?.[FieldType.Number]
          }
          getOperationOptionRenderer={
            getOperationOptionRenderer?.NumberFilterOperationType
          }
        />
      );
    case FieldType.Text:
    case FieldType.MultiLineText:
      return (
        <TextCriteria
          filter={filter as TextFieldFilter}
          onChange={onChange}
          getFilterOperationTypeOptions={
            getFilterOperationTypeOptions?.[FieldType.Text]
          }
          getOperationOptionRenderer={
            getOperationOptionRenderer?.TextFieldFilterOperationType
          }
        />
      );
    case FieldType.Date:
      return (
        <Form.Item>
          <DateCriteria
            filter={filter as DateFieldFilter}
            onChange={onChange}
            getFilterOperationTypeOptions={
              getFilterOperationTypeOptions?.[FieldType.Date]
            }
            getOperationOptionRenderer={
              getOperationOptionRenderer?.DateFieldFilterOperationType
            }
          />
        </Form.Item>
      );
    case FieldType.SingleSelect:
      return (
        <SingleSelectCriteria
          filter={filter as SingleSelectFieldFilter}
          onChange={onChange}
          components={getSelectEditorComponents?.(filter.field)}
          getFilterOperationTypeOptions={
            getFilterOperationTypeOptions?.[FieldType.SingleSelect]
          }
          getOperationOptionRenderer={
            getOperationOptionRenderer?.SelectFilterOperationType
          }
        />
      );
    case FieldType.MultiSelect:
      return filter.field.fieldName === 'responsible' ? (
        <Form.Item>
          <ResponsibleCriteria
            filter={filter as MultiSelectFieldFilter}
            onChange={onChange}
          />
        </Form.Item>
      ) : (
        <MultiSelectCriteria
          filter={filter as MultiSelectFieldFilter}
          onChange={onChange}
          components={getSelectEditorComponents?.(filter.field)}
          getFilterOperationTypeOptions={
            getFilterOperationTypeOptions?.[FieldType.MultiSelect]
          }
          getOperationOptionRenderer={
            getOperationOptionRenderer?.SelectFilterOperationType
          }
        />
      );
    case FieldType.SingleUser:
      return (
        <SingleUserCriteria
          filter={filter as SingleUserFieldFilter}
          onChange={onChange}
          components={getSelectEditorComponents?.(filter.field)}
        />
      );
    case FieldType.MultiUser:
      return (
        <MultiUserCriteria
          filter={filter as MultiUserFieldFilter}
          onChange={onChange}
          components={getSelectEditorComponents?.(filter.field)}
        />
      );
    case FieldType.Assignee:
      return (
        <AssigneeCriteria
          filter={filter as AssigneeFieldFilter}
          onChange={onChange}
        />
      );
    case FieldType.Estimate:
      return (
        <Form.Item>
          <EstimateCriteria
            filter={filter as EstimateFieldFilter}
            onChange={onChange}
          />
        </Form.Item>
      );
    default:
      return unreachable(filter.field);
  }
};
